import { useEffect, useRef, useState } from 'react';
import type {
  DefaultValue,
  EnumDef,
  FieldConstraints,
  FieldDef,
  FieldType,
  PrimitiveType,
  TypeDef,
} from '../types/model';
import { PRIMITIVES, getStringValues, isPlainObject } from '../utils/modelUtils';

type FieldRowProps = {
  field: FieldDef;
  typeOptions: TypeDef[];
  enumOptions: EnumDef[];
  onUpdate: (patch: Partial<FieldDef>) => void;
  onUpdateConstraints: (patch: Partial<FieldConstraints>) => void;
  onDelete: () => void;
  onSelectType: (typeId: string) => void;
  onJsonErrorChange: (fieldId: string, hasError: boolean) => void;
  isFocused: boolean;
  onFocusHandled: () => void;
  isDefaultCleared: boolean;
  onDefaultUpdated: () => void;
  onNameEmptyChange: (fieldId: string, isEmpty: boolean) => void;
  isNameTaken: (name: string) => boolean;
};

const formatDefaultValue = (field: FieldDef) => {
  if (field.defaultValue === null || field.defaultValue === undefined) {
    return '';
  }
  if (typeof field.defaultValue === 'string') {
    return field.defaultValue;
  }
  if (typeof field.defaultValue === 'number') {
    return field.defaultValue.toString();
  }
  if (typeof field.defaultValue === 'boolean') {
    return field.defaultValue ? 'true' : 'false';
  }
  return '';
};

export const FieldRow = ({
  field,
  typeOptions,
  enumOptions,
  onUpdate,
  onUpdateConstraints,
  onDelete,
  onSelectType,
  onJsonErrorChange,
  isFocused,
  onFocusHandled,
  isDefaultCleared,
  onDefaultUpdated,
  onNameEmptyChange,
  isNameTaken,
}: FieldRowProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const usesJsonInput = false;
  const [jsonText, setJsonText] = useState('');
  const [nameDraft, setNameDraft] = useState(field.name);
  const [isNameDirty, setIsNameDirty] = useState(false);

  useEffect(() => {
    if (usesJsonInput) {
      if (field.defaultValue === null || field.defaultValue === undefined) {
        setJsonText('');
      } else {
        setJsonText(JSON.stringify(field.defaultValue, null, 2));
      }
    }
  }, [field.defaultValue, usesJsonInput]);

  useEffect(() => {
    if (isFocused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onFocusHandled();
    }
  }, [isFocused, onFocusHandled]);

  useEffect(() => {
    if (!usesJsonInput) {
      onJsonErrorChange(field.id, false);
    }
  }, [field.id, onJsonErrorChange, usesJsonInput]);

  useEffect(() => {
    if (!isNameDirty) {
      setNameDraft(field.name);
    }
  }, [field.id, field.name, isNameDirty]);

  useEffect(() => {
    onNameEmptyChange(field.id, nameDraft.trim().length === 0);
  }, [field.id, nameDraft, onNameEmptyChange]);

  const handleJsonChange = (value: string) => {
    setJsonText(value);
    if (!value.trim()) {
      onUpdate({ defaultValue: null });
      onDefaultUpdated();
      onJsonErrorChange(field.id, false);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (field.isArray) {
        if (!Array.isArray(parsed)) {
          throw new Error('Expected array');
        }
      } else if (!isPlainObject(parsed)) {
        throw new Error('Expected object');
      }
      onUpdate({ defaultValue: parsed as DefaultValue });
      onDefaultUpdated();
      onJsonErrorChange(field.id, false);
    } catch {
      onJsonErrorChange(field.id, true);
    }
  };

  const handleKindChange = (kind: FieldType['kind']) => {
    if (kind === 'primitive') {
      onUpdate({ type: { kind: 'primitive', primitive: 'string' } });
      return;
    }
    if (kind === 'enum') {
      onUpdate({ type: { kind: 'enum', enumId: enumOptions[0]?.id ?? '' } });
      return;
    }
    if (kind === 'reference') {
      onUpdate({ type: { kind: 'reference', typeId: typeOptions[0]?.id ?? '' } });
      return;
    }
    onUpdate({ type: { kind: 'type', typeId: typeOptions[0]?.id ?? '' } });
  };

  const handleEnumDefaultToggle = (valueId: string) => {
    const current = getStringValues(field.defaultValue);
    const next = current.includes(valueId) ? current.filter((item) => item !== valueId) : [...current, valueId];
    onUpdate({ defaultValue: next });
    onDefaultUpdated();
  };

  const renderDefaultInput = () => {
    const typeField = field.type;
    if (typeField.kind === 'type') {
      const typeId = typeField.typeId;
      const targetType = typeOptions.find((entry) => entry.id === typeId);
      return (
        <div className="nested-default">
          <div className="muted">Defaults for nested types are edited inside the referenced type.</div>
          <div className="nested-actions">
            <button
              className="button"
              type="button"
              disabled={!typeId}
              onClick={() => onSelectType(typeId)}
            >
              Open {targetType?.name || 'type'}
            </button>
            <span className="muted">No default value set.</span>
          </div>
        </div>
      );
    }

    if (field.isArray && field.type.kind !== 'type') {
      return <div className="muted">Default values are disabled for arrays.</div>;
    }

    if (field.isArray && field.type.kind === 'enum') {
      const enumId = field.type.enumId;
      const enumEntry = enumOptions.find((entry) => entry.id === enumId);
      const selected = getStringValues(field.defaultValue);
      return (
        <div className="enum-checklist">
          {(enumEntry?.values ?? []).map((value) => (
            <label key={value.id} className="checkline">
              <input
                type="checkbox"
                checked={selected.includes(value.id)}
                onChange={() => handleEnumDefaultToggle(value.id)}
              />
              <span>{value.label || 'Untitled value'}</span>
            </label>
          ))}
          {!enumEntry?.values?.length && <div className="muted">No enum values available.</div>}
        </div>
      );
    }

    if (usesJsonInput) {
      return (
        <textarea
          className="input mono"
          rows={4}
          placeholder="Enter a JSON array"
          value={jsonText}
          onChange={(event) => handleJsonChange(event.target.value)}
        />
      );
    }

    if (field.type.kind === 'enum') {
      const enumId = field.type.enumId;
      const enumEntry = enumOptions.find((entry) => entry.id === enumId);
      const selected = typeof field.defaultValue === 'string' ? field.defaultValue : '';
      return (
        <select
          className="input"
          value={selected}
          onChange={(event) => {
            const value = event.target.value;
            onUpdate({ defaultValue: value ? value : null });
            onDefaultUpdated();
          }}
        >
          <option value="">No default</option>
          {(enumEntry?.values ?? []).map((value) => (
            <option key={value.id} value={value.id}>
              {value.label || 'Untitled value'}
            </option>
          ))}
        </select>
      );
    }

    if (field.type.kind === 'primitive') {
      if (field.type.primitive === 'boolean') {
        const selected = typeof field.defaultValue === 'boolean' ? (field.defaultValue ? 'true' : 'false') : '';
        return (
          <select
            className="input"
            value={selected}
            onChange={(event) => {
              const value = event.target.value;
              if (!value) {
                onUpdate({ defaultValue: null });
              } else {
                onUpdate({ defaultValue: value === 'true' });
              }
              onDefaultUpdated();
            }}
          >
            <option value="">No default</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      }

      if (field.type.primitive === 'int' || field.type.primitive === 'float') {
        const numericValue = typeof field.defaultValue === 'number' ? field.defaultValue : '';
        return (
          <input
            className="input"
            type="number"
            value={numericValue}
            onChange={(event) => {
              const value = event.target.value;
              onUpdate({ defaultValue: value === '' ? null : Number(value) });
              onDefaultUpdated();
            }}
          />
        );
      }

      return (
        <input
          className="input"
          type="text"
          value={formatDefaultValue(field)}
          onChange={(event) => {
            onUpdate({ defaultValue: event.target.value });
            onDefaultUpdated();
          }}
        />
      );
    }

    if (field.type.kind === 'reference') {
      return (
        <input
          className="input"
          type="text"
          value={formatDefaultValue(field)}
          onChange={(event) => {
            onUpdate({ defaultValue: event.target.value });
            onDefaultUpdated();
          }}
        />
      );
    }

    return null;
  };

  return (
    <div className={`field-card ${isFocused ? 'focus' : ''}`} ref={cardRef}>
      <div className="field-row">
        <div className="field-column">
          <label className="label">Field name</label>
          <input
            className="input"
            type="text"
            value={nameDraft}
            onChange={(event) => {
              const nextName = event.target.value;
              setNameDraft(nextName);
              setIsNameDirty(true);
              if (nextName.trim() && !isNameTaken(nextName)) {
                onUpdate({ name: nextName });
              }
            }}
            onBlur={() => {
              if (nameDraft.trim().length > 0) {
                setIsNameDirty(false);
              }
            }}
            placeholder="e.g. status"
          />
          {nameDraft.trim().length === 0 && <div className="warning-text">Name is required.</div>}
          {nameDraft.trim().length > 0 && isNameTaken(nameDraft) && (
            <div className="warning-text">Name must be unique.</div>
          )}
        </div>
        <div className="field-column">
          <label className="label">Type</label>
          <div className="type-select">
            <select
              className="input"
              value={field.type.kind}
              onChange={(event) => handleKindChange(event.target.value as FieldType['kind'])}
            >
              <option value="primitive">Primitive</option>
              <option value="enum">Enum</option>
              <option value="type">Type</option>
              <option value="reference">Reference</option>
            </select>
            {field.type.kind === 'primitive' && (
              <select
                className="input"
                value={field.type.primitive}
                onChange={(event) =>
                  onUpdate({ type: { kind: 'primitive', primitive: event.target.value as PrimitiveType } })
                }
              >
                {PRIMITIVES.map((primitive) => (
                  <option key={primitive} value={primitive}>
                    {primitive}
                  </option>
                ))}
              </select>
            )}
            {field.type.kind === 'enum' && (
              <select
                className="input"
                value={field.type.enumId}
                onChange={(event) => onUpdate({ type: { kind: 'enum', enumId: event.target.value } })}
              >
                {enumOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name || 'Unnamed enum'}
                  </option>
                ))}
                {enumOptions.length === 0 && <option value="">No enums available</option>}
              </select>
            )}
            {field.type.kind === 'type' && (
              <select
                className="input"
                value={field.type.typeId}
                onChange={(event) => onUpdate({ type: { kind: 'type', typeId: event.target.value } })}
              >
                {typeOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name || 'Unnamed type'}
                  </option>
                ))}
                {typeOptions.length === 0 && <option value="">No types available</option>}
              </select>
            )}
            {field.type.kind === 'reference' && (
              <select
                className="input"
                value={field.type.typeId}
                onChange={(event) => onUpdate({ type: { kind: 'reference', typeId: event.target.value } })}
              >
                {typeOptions.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name || 'Unnamed type'}
                  </option>
                ))}
                {typeOptions.length === 0 && <option value="">No types available</option>}
              </select>
            )}
          </div>
        </div>
        <div className="field-column compact">
          <label className="label">Array</label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={field.isArray}
              onChange={(event) => onUpdate({ isArray: event.target.checked })}
            />
            <span>{field.isArray ? 'Array' : 'Single'}</span>
          </label>
        </div>
        <div className="field-column compact">
          <label className="label">Required</label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={field.isRequired}
              onChange={(event) => onUpdate({ isRequired: event.target.checked })}
            />
            <span>{field.isRequired ? 'Required' : 'Optional'}</span>
          </label>
        </div>
        <div className="field-column compact">
          <label className="label">Actions</label>
          <button className="button danger" type="button" onClick={onDelete}>
            Remove
          </button>
        </div>
      </div>
      <div className="field-row">
        <div className="field-column">
          <label className="label">Default value</label>
          {renderDefaultInput()}
          {isDefaultCleared && !field.isArray && <div className="warning-text">Default was cleared. Choose a new default.</div>}
        </div>
        <div className="field-column constraints">
          <label className="label">Constraints</label>
          <div className="constraint-grid">
            {(field.type.kind === 'primitive' && field.type.primitive === 'string') ||
              field.type.kind === 'reference' ? (
              <>
                <input
                  className="input"
                  type="number"
                  placeholder="Min length"
                  value={field.constraints.minLength ?? ''}
                  onChange={(event) =>
                    onUpdateConstraints({
                      minLength: event.target.value === '' ? undefined : Number(event.target.value),
                    })
                  }
                />
                <input
                  className="input"
                  type="number"
                  placeholder="Max length"
                  value={field.constraints.maxLength ?? ''}
                  onChange={(event) =>
                    onUpdateConstraints({
                      maxLength: event.target.value === '' ? undefined : Number(event.target.value),
                    })
                  }
                />
              </>
            ) : null}
            {field.type.kind === 'primitive' &&
              (field.type.primitive === 'int' || field.type.primitive === 'float') && (
                <>
                  <input
                    className="input"
                    type="number"
                    placeholder="Min value"
                    value={field.constraints.minValue ?? ''}
                    onChange={(event) =>
                      onUpdateConstraints({
                        minValue: event.target.value === '' ? undefined : Number(event.target.value),
                      })
                    }
                  />
                  <input
                    className="input"
                    type="number"
                    placeholder="Max value"
                    value={field.constraints.maxValue ?? ''}
                    onChange={(event) =>
                      onUpdateConstraints({
                        maxValue: event.target.value === '' ? undefined : Number(event.target.value),
                      })
                    }
                  />
                </>
              )}
            {field.isArray && (
              <>
                <input
                  className="input"
                  type="number"
                  placeholder="Min items"
                  value={field.constraints.minItems ?? ''}
                  onChange={(event) =>
                    onUpdateConstraints({
                      minItems: event.target.value === '' ? undefined : Number(event.target.value),
                    })
                  }
                />
                <input
                  className="input"
                  type="number"
                  placeholder="Max items"
                  value={field.constraints.maxItems ?? ''}
                  onChange={(event) =>
                    onUpdateConstraints({
                      maxItems: event.target.value === '' ? undefined : Number(event.target.value),
                    })
                  }
                />
              </>
            )}
            {!field.isArray &&
              field.type.kind !== 'primitive' &&
              field.type.kind !== 'enum' &&
              field.type.kind !== 'type' &&
              field.type.kind !== 'reference' && <div className="muted">No constraints available.</div>}
            {field.type.kind === 'enum' && !field.isArray && <div className="muted">Enums do not have constraints.</div>}
            {field.type.kind === 'type' && !field.isArray && <div className="muted">Objects do not have constraints.</div>}
            {field.type.kind === 'reference' && !field.isArray && (
              <div className="muted">References use string length constraints.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
